import type { PureQueryOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SOItemListV4 } from '~/components/supporting-object/so-item-list-v4';
import { TaggedSOItemListV4 } from '~/components/supporting-object/tagged-so-item-list-v4';
import { useRouteParam } from '~/hooks/routing';

const OUTCROP_CROSS_SECTIONS_PAGE = graphql(`
  query OutcropCrossSectionsPage($id: ID!) {
    outcropGet(id: $id) {
      id
      crossSections {
        ...crossSectionParts
        pictures {
          ...soPictureParts
        }
        files {
          ...fileParts
        }
        georeferences {
          ...georeferenceParts
        }
      }

      studies {
        ...studyParts
        crossSections {
          ...crossSectionParts
          pictures {
            ...soPictureParts
          }
          files {
            ...fileParts
          }
          georeferences {
            ...georeferenceParts
          }
        }
      }
    }
  }
`);

export default function OutcropCrossSectionsPage() {
  return (
    <ApolloProviderV4>
      <OutcropCrossSectionsPageInner />
    </ApolloProviderV4>
  );
}

function OutcropCrossSectionsPageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_CROSS_SECTIONS_PAGE, {
    variables: { id: outcropId },
  });

  const refetchQueries: PureQueryOptions[] = [
    { query: OUTCROP_CROSS_SECTIONS_PAGE, variables: { outcropId } },
  ];

  const outcrop = data?.outcropGet;
  const crossSections = outcrop?.crossSections ?? [];
  const outcropStudies =
    outcrop?.studies.slice().sort(R.ascend(s => s.name)) ?? [];

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <div className="space-y-4">
      <SOItemListV4
        items={crossSections}
        soParentType="outcrop"
        soParentId={outcropId}
        bookmarkParentType={BookmarkParentType.CrossSection}
        refetchQueries={refetchQueries}
      />

      <TaggedSOItemListV4
        outcropId={outcropId}
        studies={outcropStudies}
        supportObjectKey="crossSections"
        refetchQueries={refetchQueries}
      />
    </div>
  );
}
